"use client";

import { cn } from "@/lib/utils";
import type { VolleyballTemplateData } from "../volleyball-template";
import {
  ContactShadow,
  CourtNet,
  FieldMatchFrame,
  type FieldMatchState,
  FieldScore,
  FloatingProp,
  PerspectivePlane,
  type PlaneSpec,
  projectOnPlane,
} from "./field-sport-scene-kit";

type VolleyballDisplayPreviewProps = {
  data: VolleyballTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

// Beach court at golden hour, broadcast view across the net.
const SAND: PlaneSpec = {
  top: 70,
  depth: 170,
  tilt: 58,
  perspective: 320,
  farWidth: 110,
};
const BALL_SPOT = projectOnPlane(SAND, 0.5, 64);

/** Mikasa-style tricolour panels. */
const Volleyball = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-full bg-[radial-gradient(circle_at_34%_28%,#ffffff_0%,#f4f1e8_40%,#bdb6a4_100%)]",
      className,
    )}
    style={{
      boxShadow:
        "inset -2px -3px 5px rgba(60,40,10,0.35), inset 1px 2px 3px rgba(255,255,255,0.9)",
    }}
  >
    <div
      className="absolute inset-0 bg-[#f5c518]/85"
      style={{ clipPath: "polygon(0% 0%, 62% 0%, 40% 48%, 0% 30%)" }}
    />
    <div
      className="absolute inset-0 bg-[#1f4fb4]/80"
      style={{ clipPath: "polygon(60% 52%, 100% 38%, 100% 100%, 48% 100%)" }}
    />
    <div className="absolute top-[-20%] bottom-[-20%] left-[-36%] w-[76%] rounded-[100%] border border-black/20" />
    <div className="absolute top-[-20%] right-[-36%] bottom-[-20%] w-[76%] rounded-[100%] border border-black/20" />
    <div className="absolute top-[10%] left-[18%] h-[30%] w-[34%] rounded-full bg-white/70 blur-[1px]" />
  </div>
);

/** Palm silhouette for the beach's edges. */
function Palm({ className, flip }: { className?: string; flip?: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 60 80"
      className={cn("absolute", flip && "-scale-x-100", className)}
    >
      <path
        d="M30 80 C31 62 33 44 30 26"
        stroke="#1a0f1e"
        strokeWidth="3.2"
        fill="none"
        strokeLinecap="round"
      />
      <g fill="#1a0f1e">
        <path d="M30 26 C20 18 8 20 1 30 C10 24 20 24 30 28 Z" />
        <path d="M30 26 C24 12 14 6 4 8 C14 12 22 18 30 28 Z" />
        <path d="M30 26 C34 12 44 6 56 8 C44 12 36 18 30 28 Z" />
        <path d="M30 26 C40 18 52 20 59 30 C50 24 40 24 30 28 Z" />
        <path d="M30 26 C30 14 34 4 40 0 C36 8 33 16 31 28 Z" />
      </g>
    </svg>
  );
}

/** Sand, tape lines and footprints, flat in plane coordinates. */
function SandCourt() {
  const footprints = [
    { u: 0.24, v: 54, r: -20 },
    { u: 0.27, v: 64, r: -14 },
    { u: 0.66, v: 96, r: 18 },
    { u: 0.7, v: 104, r: 12 },
    { u: 0.38, v: 118, r: 4 },
    { u: 0.81, v: 46, r: -8 },
  ];
  return (
    <>
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#d9b07a_0%,#e7c48f_45%,#f0d3a2_100%)]" />
      {/* Grain: three dot layers at odd pitches so they never tile visibly */}
      <div className="absolute inset-0 [background-image:radial-gradient(rgba(120,80,30,0.32)_0.6px,transparent_0.9px),radial-gradient(rgba(255,250,235,0.55)_0.6px,transparent_0.9px),radial-gradient(rgba(150,100,40,0.22)_0.8px,transparent_1.2px)] [background-size:3px_3px,3.7px_3.3px,5.3px_4.7px] [background-position:0_0,1.3px_1.7px,2.1px_0.6px] opacity-45" />
      {/* Wind ripples */}
      <div className="absolute inset-0 [background-image:repeating-linear-gradient(176deg,transparent_0px,transparent_7px,rgba(140,96,44,0.16)_7px,rgba(140,96,44,0.16)_8px,rgba(255,244,220,0.18)_8px,rgba(255,244,220,0.18)_9px)] opacity-50" />
      {footprints.map((f) => (
        <div
          key={`${f.u}-${f.v}`}
          className="absolute h-[5px] w-[9px] -translate-x-1/2 rounded-[50%] bg-[radial-gradient(closest-side,rgba(120,80,30,0.45),rgba(120,80,30,0.1)_70%,transparent)] shadow-[0_1px_0_rgba(255,244,220,0.5)]"
          style={{ left: `${f.u * 100}%`, top: f.v, rotate: `${f.r}deg` }}
        />
      ))}
      {/* Court: blue boundary tape, centre line under the net */}
      <div className="absolute top-[18px] right-[8%] left-[8%] h-[118px] border-[2.5px] border-[#2d6fe0] shadow-[0_0_0_1px_rgba(255,255,255,0.25)]" />
      <div className="absolute top-[18px] left-1/2 h-[118px] w-[2px] -translate-x-1/2 bg-[#2d6fe0]/80" />
    </>
  );
}

function BeachScene() {
  return (
    <>
      {/* Golden-hour sky */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#25317a_0%,#6b3d8f_20%,#d2577a_38%,#ffad66_56%,#ffd59a_70%)]" />
      {/* Setting sun, half into the sea */}
      <div className="absolute top-[34px] left-[80%] h-[34px] w-[34px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,#fff8d6_0%,#ffd27a_55%,#ff9a4a_100%)] shadow-[0_0_24px_10px_rgba(255,190,110,0.55)]" />
      {/* Sea: deep band, sparkle, sun's reflection column */}
      <div className="absolute inset-x-0 top-[52px] h-[22px] bg-[linear-gradient(180deg,#3a4f9a_0%,#4f72b6_60%,#7c8fc0_100%)]" />
      <div className="absolute inset-x-0 top-[54px] h-[18px] [background-image:repeating-linear-gradient(180deg,transparent_0px,transparent_2px,rgba(255,220,170,0.35)_2px,rgba(255,220,170,0.35)_2.6px)] [mask-image:linear-gradient(90deg,transparent,#000_20%,#000_80%,transparent)] opacity-70" />
      <div className="absolute top-[52px] left-[80%] h-[22px] w-[30px] -translate-x-1/2 bg-[repeating-linear-gradient(180deg,rgba(255,230,170,0.85)_0px,rgba(255,230,170,0.85)_1.5px,transparent_1.5px,transparent_3.5px)] [mask-image:radial-gradient(closest-side,#000,transparent)]" />
      {/* Shoreline foam */}
      <div className="absolute inset-x-0 top-[70px] h-[4px] bg-[linear-gradient(180deg,rgba(255,255,255,0.65),rgba(255,255,255,0))]" />

      <Palm className="top-[2px] left-[-6px] h-[74px] w-[56px]" />
      <Palm
        className="top-[16px] left-[30px] h-[56px] w-[42px] opacity-90"
        flip
      />
      <Palm className="top-[6px] right-[-4px] h-[70px] w-[52px]" flip />

      <PerspectivePlane plane={SAND}>
        <SandCourt />
      </PerspectivePlane>

      {/* Warm key light from the sun, cool shade in the foreground */}
      <div className="absolute inset-0 bg-[linear-gradient(105deg,transparent_35%,rgba(255,170,90,0.16)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_64%,rgba(70,30,40,0.32)_100%)]" />

      <CourtNet
        plane={SAND}
        farV={10}
        nearV={146}
        height={29}
        drop={12}
        postClassName="fill-[#f2efe6]"
        meshColor="rgba(20,20,30,0.7)"
      />

      <div className="absolute inset-0 bg-[radial-gradient(ellipse_85%_100%_at_50%_45%,transparent_60%,rgba(40,10,30,0.4)_100%)]" />
    </>
  );
}

export function VolleyballDisplayPreview({
  data,
  participants,
}: VolleyballDisplayPreviewProps) {
  const toSide = (team: "your-team" | "opponent-team") =>
    participants
      .filter((participant) => data?.positions?.[participant.id] === team)
      .map((participant) => ({
        ...participant,
        handle: participant.username ?? undefined,
      }));
  const yourTeam = toSide("your-team");
  const oppTeam = toSide("opponent-team");

  const showScore = !!data && data.hasScore !== false;
  const yourWon =
    !!data && data.matchScore.yourSets > data.matchScore.opponentSets;
  const opponentWon =
    !!data && data.matchScore.opponentSets > data.matchScore.yourSets;

  const matchState: FieldMatchState =
    yourTeam.length === 0 && oppTeam.length === 0
      ? "EMPTY"
      : showScore
        ? "FULL"
        : "NO_SCORE";

  const totalSets = data
    ? data.matchScore.yourSets + data.matchScore.opponentSets
    : 0;

  return (
    <FieldMatchFrame
      label="Volleyball"
      frameClassName="bg-[#3a2340]"
      matchState={matchState}
      scene={<BeachScene />}
      yourTeam={yourTeam}
      opponentTeam={oppTeam}
      yourWinner={showScore && yourWon}
      opponentWinner={showScore && opponentWon}
      linkToProfiles
      bandClassName="top-[56px] bottom-1"
      decor={
        matchState !== "NO_SCORE" ? null : (
          // Serve: the ball hangs high over the net.
          <div
            className="absolute -translate-x-1/2"
            style={{ left: BALL_SPOT.left, top: BALL_SPOT.top - 4 }}
          >
            <ContactShadow className="top-[46px] left-1/2 h-[6px] w-[18px] -translate-x-1/2 opacity-70" />
            <FloatingProp amplitude={4} duration={3.8}>
              <Volleyball className="h-[20px] w-[20px] @sm:h-[24px] @sm:w-[24px]" />
            </FloatingProp>
          </div>
        )
      }
      center={
        matchState === "FULL" && data ? (
          <>
            <FieldScore
              transitionKey={totalSets}
              left={data.matchScore.yourSets}
              right={data.matchScore.opponentSets}
              leftWinner={yourWon}
              rightWinner={opponentWon}
            />
            {data.sets && data.sets.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-1">
                {data.sets
                  .map((set, n) => ({ ...set, id: `set-${n + 1}` }))
                  .slice(-5)
                  .map((set) => {
                    const yourSetWon = set.yourScore > set.opponentScore;
                    return (
                      <span
                        key={set.id}
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide tabular-nums ring-1 @sm:text-[11px]",
                          yourSetWon
                            ? "bg-black/50 text-white ring-white/20"
                            : "bg-black/35 text-white/65 ring-white/10",
                        )}
                      >
                        {set.yourScore}-{set.opponentScore}
                      </span>
                    );
                  })}
              </div>
            ) : null}
          </>
        ) : null
      }
    />
  );
}
